import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoanPartner, LoanPayload, LoanPostStatusResponse, LoanStatus, NewLoansService } from '../new-loans.service';
import { catchError, map, of, tap } from 'rxjs';
import { SimpleGlobalModalService } from 'libs/desktop/data-access/core-services/src/lib/GlobalModal/simple-global-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ActionCardConfigInterface } from '@brightside/brightside-ui';

@Component({
    selector: 'brightside-web-loan-dashboard',
    templateUrl: './loan-dashboard.component.html',
    styleUrls: ['./loan-dashboard.component.scss'],
    standalone: false
})
export class LoanDashboardComponent implements OnInit {
  loan: LoanPostStatusResponse;
  partner: LoanPartner;
  partnerImageUrl: string;
  LoanStatus = LoanStatus;

  findMoneyCardInfo: ActionCardConfigInterface = {
    title: this._translateService.instant('LOAN_FIND_MONEY_CARD_TITLE'),
    body: this._translateService.instant('LOAN_FIND_MONEY_CARD_BODY'),
    cardStyle: 'small',
    image: '../assets/ld_find_money_card.png',
    backgroundImagePosition: 'right',
    removeMaxWidth: true,
    marginBottom: 'medium',
    bgColor: '#fff',
  };

  constructor(
    private _router: Router,
    private _loansService: NewLoansService,
    private _route: ActivatedRoute,
    private _modalService: SimpleGlobalModalService,
    private _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadLoanInformation();
  }

  backToHome(): void {
    this._router.navigate(['home']);
  }

  loadLoanInformation(): void {
    const partnerName = this._route.snapshot.queryParamMap.get('vendor') || '';

    this._loansService
      .getLoanByPartnerType(partnerName)
      .pipe(
        map((res) => (res as LoanPayload)?.payload),
        tap((res) => {
          this.handleSuccessResponse(res);
        }),
        catchError(() => of(this.handleErrorResponse()))
      )
      .subscribe();
  }

  onFindMoneyCardClick(): void {
    this._router.navigate(['find-money', 'intro']);
  }

  private handleSuccessResponse(data: { loans: LoanPostStatusResponse[]; partner: LoanPartner }): void {
    this.partner = data.partner;
    let loan = data.loans.filter((loan) => loan.isTaken)[0];
    if (!loan) {
      loan = data.loans?.filter((loan) =>
        [
          LoanStatus.LOAN_PROCESS_PENDING,
          LoanStatus.LOAN_PENDING,
          LoanStatus.ACCOUNT_CREATED,
          LoanStatus.LOAN_APPROVED,
          LoanStatus.LOAN_FUNDED,
          LoanStatus.LOAN_PROCESS_ERROR,
          LoanStatus.LOAN_OFFERED,
          LoanStatus.APPLICATION_TECHNICAL_ISSUE,
          LoanStatus.USER_ACTION_REQUIRED,
          LoanStatus.APPLICATION_REVIEW,
          LoanStatus.LOAN_ACCEPTED,
        ].includes(loan.status)
      )[0];
    }
    this.loan = loan;
    this.setStatusDisplayName(this.loan.status);
    this.partner.link = this.loan.partner?.link;
    this.partnerImageUrl = `../assets/${this.partner.displayName.toLowerCase()}.png`;
  }

  private setStatusDisplayName(status: LoanStatus) {
    switch (status) {
      case LoanStatus.LOAN_APPROVED:
        this.loan.statusDisplayName = 'Active';
        break;
      case LoanStatus.LOAN_PAID_OFF:
        this.loan.statusDisplayName = 'Repaid';
        break;
      default:
        this.loan.statusDisplayName = '';
        break;
    }
  }

  private handleErrorResponse(): void {
    this._modalService.createModal({
      title: 'error_general_title'.toUpperCase(),
      contentKey: 'error_general_desc'.toUpperCase(),
      displayCloseButton: false,
      eventName: 'loan_dashboard',
      isError: true,
      size: 'medium',
      ctaKey: 'RETRY',
      ctaAction: () => {
        this.loadLoanInformation();
      },
    });
  }
}
